// CommandPalette — single input that's both a slash-command palette
// and a project search. Typing `/` shows commands; anything else is
// fuzzy-searched against project schema. Replaces SearchBar on
// canvas/workspace pages.
//
// Dropdown item shape:
//   { kind: 'cmd', name, description, args, snippet }
//   { kind: 'item', type, key, score }
//
// Global shortcut: Cmd/Ctrl+K focuses the input.

import { commandDefs, commands } from './CommandBar.js'

export const CommandPalette = {
  // FA204 / frank trap — every helper below used to be a bare module-scope
  // function read from inside `onFocus`/`onInput`/`onBlur`/`onKeydown`/
  // `onClick` (serialized factories); they silently read as undefined once
  // this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Moved into `scope`,
  // called via `el.scope.<name>`; siblings call each other via `this.<name>`
  // (el.scope.foo() binds `this` to el.scope — object-literal properties
  // can't see each other as bare identifiers otherwise). `commandDefs`/
  // `commands` stay a cross-file import (untouched — out of scope for this
  // ticket, governed by the separate el.call() convention).
  scope: {
    MAX_DROPDOWN: 20,

    // `commandDefs[].description` is a bundle KEY (CommandBar.js) — resolve it
    // HERE, at produce time, because renderDropdown writes the value straight
    // into innerHTML and has no reactive effect to re-run. `el` is threaded in
    // for the same reason: polyglot is an element-bound call.
    filterCommands(el, query) {
      const q = query.trim().toLowerCase()
      const tag = (c) => ({
        kind: 'cmd',
        name: c.name,
        description: el.call('polyglot', c.description || ''),
        args: c.args
      })
      if (!q) return commandDefs.slice(0, this.MAX_DROPDOWN).map(tag)
      return commandDefs
        .map(tag)
        .filter((c) => c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q))
        .slice(0, this.MAX_DROPDOWN)
    },

    fuzzySearchItems(el, query) {
      const q = query.trim().toLowerCase()
      if (!q) return []
      const data = el.getRootState?.()?.data || el.state?.root?.data
      if (!data) return []
      const out = []
      const scan = (obj, type) => {
        if (!obj) return
        for (const key of Object.keys(obj)) {
          if (key.toLowerCase().includes(q)) out.push({ kind: 'item', type, key })
          if (out.length >= this.MAX_DROPDOWN) return
        }
      }
      scan(data.pages, 'pages')
      scan(data.components, 'components')
      scan(data.functions, 'functions')
      return out.slice(0, this.MAX_DROPDOWN)
    },

    runCommand(el, input) {
      const value = input.trim()
      if (!value.startsWith('/')) return null
      const [name, ...args] = value.slice(1).split(/\s+/)
      const handler = commands[name.toLowerCase()]
      const t = (k) => el.call('polyglot', k)
      if (!handler) return t('shell.commandPalette.unknown').replace('{name}', name)
      try {
        return handler(el, args) || t('shell.commandPalette.ran').replace('{name}', name)
      } catch (e) {
        return t('shell.commandPalette.error').replace('{error}', e.message || e)
      }
    },

    highlightIndex(palette, delta) {
      const items = palette.Dropdown?.node?.querySelectorAll('[data-cp-item]')
      if (!items?.length) return
      const cur = Array.from(items).findIndex((i) => i.hasAttribute('data-cp-active'))
      items.forEach((i) => i.removeAttribute('data-cp-active'))
      const next = ((cur < 0 ? -1 : cur) + delta + items.length) % items.length
      const target = items[Math.max(0, next)]
      target?.setAttribute('data-cp-active', '1')
      target?.style?.setProperty?.('background', 'var(--color-selected)')
      Array.from(items).forEach((i, idx) => {
        if (idx !== next) i.style?.setProperty?.('background', '')
      })
    },

    executeHighlighted(palette, el) {
      const active =
        palette.Dropdown?.node?.querySelector('[data-cp-item][data-cp-active]') ||
        palette.Dropdown?.node?.querySelector('[data-cp-item]')
      if (!active) return null
      const kind = active.getAttribute('data-cp-kind')
      const name = active.getAttribute('data-cp-name')
      if (kind === 'cmd') {
        palette.Input.node.value = '/' + name + ' '
        palette.Input.node.focus()
        this.updateDropdown(palette, el)
        return null
      }
      if (kind === 'item') {
        const key = active.getAttribute('data-cp-key')
        const type = active.getAttribute('data-cp-type')
        el.call('openArtboardItem', type, key)
        palette.Input.node.value = ''
        palette.Input.node.blur()
        this.hideDropdown(palette)
        return null
      }
      return null
    },

    hideDropdown(palette) {
      palette.Dropdown?.update?.({ style: { display: 'none' } })
    },

    showDropdown(palette) {
      palette.Dropdown?.update?.({ style: { display: 'block' } })
    },

    renderDropdown(palette, rows) {
      const node = palette.Dropdown?.node
      if (!node) return
      node.innerHTML = ''
      if (!rows.length) {
        this.hideDropdown(palette)
        return
      }
      this.showDropdown(palette)
      for (const row of rows) {
        const div = node.ownerDocument.createElement('div')
        div.setAttribute('data-cp-item', '1')
        div.setAttribute('data-cp-kind', row.kind)
        div.style.padding = '8px 12px'
        div.style.cursor = 'pointer'
        div.style.display = 'flex'
        div.style.justifyContent = 'space-between'
        div.style.gap = '12px'
        div.style.borderRadius = '4px'
        div.onmouseenter = () => {
          div.style.background = 'var(--color-hover)'
        }
        div.onmouseleave = () => {
          div.style.background = ''
        }
        if (row.kind === 'cmd') {
          div.setAttribute('data-cp-name', row.name)
          div.innerHTML =
            `<span><span style="font-family:monospace;color:#8ab4f8">/${row.name}</span>` +
            (Array.isArray(row.args)
              ? ` <span style="opacity:.55;font-size:11px">[${row.args.join('|')}]</span>`
              : '') +
            `</span><span style="opacity:.65;font-size:12px">${row.description || ''}</span>`
        } else {
          div.setAttribute('data-cp-name', row.key)
          div.setAttribute('data-cp-kind', 'item')
          div.setAttribute('data-cp-type', row.type)
          div.setAttribute('data-cp-key', row.key)
          div.innerHTML = `<span>${row.key}</span><span style="opacity:.55;font-size:11px">${row.type}</span>`
        }
        node.appendChild(div)
      }
    },

    updateDropdown(palette, el) {
      const value = palette.Input?.node?.value || ''
      if (value.startsWith('/')) {
        const rows = this.filterCommands(el, value.slice(1))
        this.renderDropdown(palette, rows)
        return
      }
      if (value.trim()) {
        const rows = this.fuzzySearchItems(el, value)
        this.renderDropdown(palette, rows)
        return
      }
      // Empty + focused: show top commands.
      this.renderDropdown(palette, this.filterCommands(el, ''))
    },

    setFeedback(palette, msg) {
      const fb = palette.ResultToast
      if (!fb?.update) return
      fb.update({ text: msg, style: { display: msg ? 'block' : 'none' } })
      if (msg) {
        clearTimeout(palette.__feedbackTimer)
        palette.__feedbackTimer = setTimeout(() => {
          fb.update({ style: { display: 'none' } })
        }, 2500)
      }
    }
  },
  tag: 'div',
  position: 'relative',
  width: '100%',
  // The `overlayLg` width token (it was the same 560 as a raw px).
  maxWidth: 'overlayLg',
  fontSize: 'Y',

  // Cmd/Ctrl+K to focus from anywhere. FA503 / Rule 64: the framework owns
  // the document-level listener — registered once when this element gets its
  // node, inert while the element is detached, and removed in dispose(). The
  // previous shape armed it from `onInit` and parked the un-binder on
  // `el.__cpUnbind`, which NOTHING ever called: every mount of this palette
  // left a live keydown listener on the document behind it.
  onDocumentKeydown: (ev, el) => {
    if (!el.node) return
    if (!(ev.metaKey || ev.ctrlKey) || ev.key?.toLowerCase() !== 'k') return
    ev.preventDefault()
    el.Input?.node?.focus()
    el.Input?.node?.select?.()
  },

  Input: {
    tag: 'input',
    type: 'text',
    placeholder: (el) => el.call('polyglot', 'shell.commandPalette.placeholder'),
    width: '100%',
    padding: 'W B',
    round: 'radiusControl',
    // The kit's `field` surface — its OWN ground and ink in both schemes, so
    // the field no longer depends on a host it cannot see (it was a fixed
    // `black.25` wash + `white` ink: 2.05:1 on a light host, measured
    // 2026-09-20). The hairline edge is the field's one kept stroke.
    theme: 'field',
    border: 'hairline solid line',
    fontSize: 'Y',
    style: { outline: 'none' },
    attr: { 'aria-label': (el) => el.call('polyglot', 'shell.commandPalette.inputAria') },
    onFocus: (ev, el) => el.scope.updateDropdown(el.parent, el.parent),
    onInput: (ev, el) => el.scope.updateDropdown(el.parent, el.parent),
    onBlur: (ev, el) => {
      // Delay so click on dropdown item can fire first.
      setTimeout(() => el.scope.hideDropdown(el.parent), 150)
    },
    onKeydown: (ev, el) => {
      const palette = el.parent
      if (ev.key === 'Escape') {
        el.node.value = ''
        el.node.blur()
        el.scope.hideDropdown(palette)
        return
      }
      if (ev.key === 'ArrowDown') {
        ev.preventDefault()
        el.scope.highlightIndex(palette, +1)
        return
      }
      if (ev.key === 'ArrowUp') {
        ev.preventDefault()
        el.scope.highlightIndex(palette, -1)
        return
      }
      if (ev.key === 'Enter') {
        ev.preventDefault()
        // Execute highlighted item, or command from input value.
        const result = el.scope.executeHighlighted(palette, palette)
        if (result === null && palette.Input.node.value.startsWith('/')) {
          const msg = el.scope.runCommand(palette, palette.Input.node.value)
          if (msg) el.scope.setFeedback(palette, msg)
          palette.Input.node.value = ''
          el.scope.hideDropdown(palette)
        }
      }
    }
  },

  Dropdown: {
    tag: 'div',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: '0',
    right: '0',
    maxHeight: '60dvh',
    overflow: 'auto',
    // The popover recipe (DESIGN_SPEC §6.1): the opaque in-app `dialog`
    // surface, the card radius, the `overlay` elevation (its ring included) —
    // it was a fixed dark hex with white ink and a white hairline, dark in
    // both schemes.
    theme: 'dialog',
    round: 'radiusCard',
    padding: 'W',
    fontSize: 'Y',
    shadow: 'overlay',
    style: {
      display: 'none',
      zIndex: '100'
    },
    onPointerDown: (ev) => ev.preventDefault(), // keep input focused while clicking
    onClick: (ev, el) => {
      const item = ev.target.closest?.('[data-cp-item]')
      if (!item) return
      const kind = item.getAttribute('data-cp-kind')
      if (kind === 'cmd') {
        const name = item.getAttribute('data-cp-name')
        el.parent.Input.node.value = '/' + name + ' '
        el.parent.Input.node.focus()
        el.scope.updateDropdown(el.parent, el.parent)
      } else if (kind === 'item') {
        const key = item.getAttribute('data-cp-key')
        const type = item.getAttribute('data-cp-type')
        el.parent.call('openArtboardItem', type, key)
        el.parent.Input.node.value = ''
        el.scope.hideDropdown(el.parent)
      }
    }
  },

  // The result toast. Its key is not a component name: a key named like a
  // registered component extends it, and `Feedback` pulled the shared
  // Feedback row (a vote toggle and a heading group) into this toast.
  ResultToast: {
    tag: 'div',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: '0',
    padding: 'W A',
    // The ink capsule (`primary`, its own adaptive ground + ink) — it was a
    // raw translucent blue under white ink.
    theme: 'primary',
    round: 'radiusPill',
    fontSize: 'Y',
    style: { display: 'none', zIndex: '99' }
  }
}
